import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import { ui } from "@/features/shell/ui-store";

/**
 * The board as an installed app: on a desktop (Chrome, Edge, Safari on a
 * Mac) and on a phone (Android, iPhone and iPad).
 *
 *   - The service worker (public/sw.js) lets it open offline, and tells the
 *     page when a new version has downloaded, so the person can switch to it.
 *   - Installing is the browser's own prompt where there is one (Chrome and
 *     Edge, desktop and Android), and a short how-to where there isn't:
 *     Safari never offers a prompt, and Firefox can't install at all.
 *   - The app icon shows the unread count.
 */

/** How this browser installs the board. */
export type InstallWay =
  /** Already running as the installed app. */
  | "installed"
  /** The browser's own install prompt is ready. */
  | "prompt"
  /** iPhone or iPad: Share, then Add to Home Screen. */
  | "ios"
  /** Safari on a Mac: File, then Add to Dock. */
  | "safari-mac"
  /** Firefox on a desktop: no installing; another browser can. */
  | "unsupported"
  /** Chrome or Edge without a prompt yet: its menu can install. */
  | "menu";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PwaState {
  /** A new version has downloaded and waits for the person to switch. */
  updateReady: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
  installed: boolean;
}

const store = createStore<PwaState>(() => ({
  updateReady: false,
  installPrompt: null,
  installed: false,
}));

export function usePwa<T>(selector: (state: PwaState) => T): T {
  return useStore(store, selector);
}

const WORKER = "/sw.js";

let waiting: ServiceWorker | null = null;
let switching = false;

function standalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // Safari on iPhone and iPad says so its own way.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Where the app was opened from: the "New task" shortcut on its icon opens
 * the board with ?new-task. Read once, before the router changes the address.
 */
export const openedFor: "new-task" | null =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("new-task")
    ? "new-task"
    : null;

function watchForUpdates(registration: ServiceWorkerRegistration): void {
  const offer = (worker: ServiceWorker | null) => {
    // Only an update: the very first install has nothing to switch from.
    if (!worker || !navigator.serviceWorker.controller) return;
    waiting = worker;
    store.setState({ updateReady: true });
  };
  offer(registration.waiting);
  registration.addEventListener("updatefound", () => {
    const worker = registration.installing;
    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed") offer(worker);
    });
  });
  // A board left open for days still hears of new versions: when it comes
  // back to the front, and every half hour.
  const check = () => {
    registration.update().catch(() => undefined);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.setInterval(check, 30 * 60 * 1000);
}

/**
 * Called once, before the app renders: the install prompt fires early and is
 * missed by anything that starts listening later.
 */
export function startPwa(): void {
  if (typeof window === "undefined") return;
  store.setState({ installed: standalone() });

  window.addEventListener("beforeinstallprompt", (event) => {
    // Kept for the board's own Install button rather than shown at once.
    event.preventDefault();
    store.setState({ installPrompt: event as BeforeInstallPromptEvent });
  });
  window.addEventListener("appinstalled", () => {
    store.setState({ installed: true, installPrompt: null });
  });

  // The installed app is already open and its icon's "New task" shortcut is
  // used: the window comes to the front (launch_handler in the manifest) and
  // is told what was asked for, rather than reloading.
  const launches = (
    window as Window & {
      launchQueue?: { setConsumer: (consumer: (params: { targetURL?: string }) => void) => void };
    }
  ).launchQueue;
  launches?.setConsumer((params) => {
    if (params.targetURL && new URL(params.targetURL).searchParams.has("new-task")) {
      ui.openNewTask();
    }
  });

  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Only after the person pressed Reload; never out of the blue.
    if (switching) window.location.reload();
  });
  // In development the page comes from Vite, not from a build to keep.
  if (!import.meta.env.PROD) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(WORKER, { scope: "/" })
      .then(watchForUpdates)
      .catch(() => undefined);
  });
}

/** Switches to the version that has downloaded: the page reloads into it. */
export function applyUpdate(): void {
  switching = true;
  if (waiting) waiting.postMessage({ type: "skip-waiting" });
  else window.location.reload();
}

export function installWay(state: Pick<PwaState, "installed" | "installPrompt">): InstallWay {
  if (state.installed) return "installed";
  if (state.installPrompt) return "prompt";
  const agent = navigator.userAgent;
  const ios =
    /iPad|iPhone|iPod/.test(agent) || (agent.includes("Macintosh") && navigator.maxTouchPoints > 1);
  if (ios) return "ios";
  if (agent.includes("Firefox/") && !agent.includes("Android")) return "unsupported";
  const chromium = ["Chrome/", "Chromium/", "Edg/"].some((name) => agent.includes(name));
  if (agent.includes("Macintosh") && agent.includes("Safari/") && !chromium) return "safari-mac";
  return "menu";
}

/** The browser's own install prompt. Resolves whether it was installed. */
export async function promptInstall(): Promise<boolean> {
  const event = store.getState().installPrompt;
  if (!event) return false;
  await event.prompt();
  const { outcome } = await event.userChoice;
  // A prompt can be used once.
  store.setState({ installPrompt: null, installed: outcome === "accepted" });
  return outcome === "accepted";
}

/** The unread count on the app's icon, where the system shows one. */
export function setAppBadge(count: number): void {
  // Chrome, Edge and Safari have it; Firefox doesn't.
  if (!("setAppBadge" in navigator)) return;
  const done = count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
  done.catch(() => undefined);
}
